import React, { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { renderRoute, routeChooseSubscriptionWithStatus, routeHome } from "@navigation/typedRoutes";
import { useLoadingController } from "@contexts/Loading.context";
import { useAuthenticationClient } from "@contexts/AuthenticationClient.context";
import { useInviteAcceptClient } from "@contexts/InviteAcceptClient.context";
import { useJwtStateHandles } from "@contexts/Jwt.context";
import { useIoState } from "@utils/useIoState";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import { toLoadingErrorInput } from "@utils/loadingError";
import * as PSAuthRules from "@ps-bridge/models/core/authRules";
import { unwrap as unwrapJwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import { ok, type Ok } from "@ps-bridge/ok";
import { mkTaskRunner } from "@ps-bridge/capabilities";
import * as Option from "@utils/Option";
import type { AuthenticationError } from "@ps-bridge/models/core/dataContracts";
import type { TaskEitherTs, TaskTs } from "@ps-bridge/interop";
import type { InviteAcceptRequest } from "@ps-bridge/controllers/inviteAccept";
import type { LoadingErrorInput } from "@ps-bridge/controllers/loading";
import type { InviteAcceptKernel } from "@ps-bridge/contracts/inviteAccept";
import { mkInviteAcceptController } from "@ps-bridge/controllers/inviteAccept";
import { buildInviteAcceptViewProps } from "@ps-bridge/inviteAccept.program";
import InviteAcceptView from "@view/invite/InviteAccept.view";
import { ioEffect, taskEitherFromEffect, taskEitherMap, taskEitherMapLeft } from "@utils/effects/branded";
import { getParamOption } from "@utils/searchParams";

const normalizeAuthError = (error: AuthenticationError): LoadingErrorInput =>
  toLoadingErrorInput(error.message, Option.none());

const InviteAcceptPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authenticationClient = useAuthenticationClient();
  const inviteAcceptClient = useInviteAcceptClient();
  const { subscriptionStatus } = useJwtStateHandles();
  const loading = useLoadingController();
  const runTaskWithError = useRunTaskWithError();

  const token = useMemo(
    () => getParamOption(searchParams, "token"),
    [searchParams],
  );

  const passwordState = useIoState("");
  const confirmPasswordState = useIoState("");
  const firstNameState = useIoState("");
  const lastNameState = useIoState("");
  const { read: password, set: setPassword } = passwordState;
  const { read: confirmPassword, set: setConfirmPassword } = confirmPasswordState;
  const { read: firstName, set: setFirstName } = firstNameState;
  const { read: lastName, set: setLastName } = lastNameState;

  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );

  const taskRunner = useMemo(() => mkTaskRunner(runTaskWithErrorIo), [runTaskWithErrorIo]);

  const acceptInvite = useMemo(
    () =>
      (request: InviteAcceptRequest): TaskEitherTs<LoadingErrorInput, Ok> =>
        inviteAcceptClient.acceptInvite(request),
    [inviteAcceptClient],
  );

  const refreshJwtToken = useMemo(
    () =>
      taskEitherMap(
        taskEitherMapLeft(
          taskEitherFromEffect(
            authenticationClient.refreshJwtToken,
            (error) => error as AuthenticationError,
          ),
          normalizeAuthError,
        ),
        () => ok,
      ),
    [authenticationClient],
  );

  const onSuccess = useMemo(
    () =>
      ioEffect(() => {
        const status = unwrapJwtSubscriptionStatus(subscriptionStatus.read);
        const redirect = PSAuthRules.subscriptionRedirect(status);
        if (redirect === "chooseSubscription") {
          navigate(renderRoute(routeChooseSubscriptionWithStatus(subscriptionStatus.read)));
        } else {
          navigate(renderRoute(routeHome));
        }
        return ok;
      }),
    [navigate, subscriptionStatus],
  );

  const controller = useMemo(
    () =>
      mkInviteAcceptController({
        token,
        password: { read: password, set: setPassword },
        confirmPassword: { read: confirmPassword, set: setConfirmPassword },
        firstName: { read: firstName, set: setFirstName },
        lastName: { read: lastName, set: setLastName },
        loading,
        acceptInvite,
        refreshJwtToken,
        taskRunner,
        onSuccess,
      }),
    [
      acceptInvite,
      confirmPassword,
      firstName,
      lastName,
      loading,
      onSuccess,
      password,
      refreshJwtToken,
      setConfirmPassword,
      setFirstName,
      setLastName,
      setPassword,
      taskRunner,
      token,
    ],
  );

  const kernel: InviteAcceptKernel = useMemo(
    () => ({
      controller,
      loading,
      form: {
        password: { read: password, set: setPassword },
        confirmPassword: { read: confirmPassword, set: setConfirmPassword },
        firstName: { read: firstName, set: setFirstName },
        lastName: { read: lastName, set: setLastName },
      },
    }),
    [
      confirmPassword,
      controller,
      firstName,
      lastName,
      loading,
      password,
      setConfirmPassword,
      setFirstName,
      setLastName,
      setPassword,
    ],
  );

  const viewProps = useMemo(
    () =>
      buildInviteAcceptViewProps({
        kernel,
        token,
      }),
    [kernel, token],
  );

  return (
    <InviteAcceptView {...viewProps} />
  );
};

export default InviteAcceptPage;
